import type { ActivationState } from "schemas/generated/activation";
import type {
  CatalogResponse,
  CollectionMetadata,
  CollectionSchemaInput,
  CollectionSchemaSnapshot,
  CreateCollectionBody,
  CreateCollectionFieldInput,
  CreateRecordInput,
  DeleteCollectionFieldInput,
  DeleteRecordInput,
  DeleteRecordResponse,
  FulfillmentOrderResponse,
  FulfillmentResponse,
  GetRecordInput,
  OrderDetailResponse,
  OrderEventListResponse,
  OrderEventResponse,
  OrderListResponse,
  OrderOverviewResponse,
  PublishCollectionInput,
  PublishCollectionResponse,
  QueryRecordsInput,
  RecordResponse,
  RefundResponse,
  UpdateCollectionFieldInput,
  UpdateCollectionInput,
  UpdateRecordInput,
} from "schemas/generated/bedrock";
import type { BrandPregenStatus } from "schemas/generated/brandPregenStatus";
import type { BrandPreview } from "schemas/generated/brandPreview";
import type { DetectedIntegrations } from "schemas/generated/detectedIntegrations";
import type { SiteDetection } from "schemas/generated/siteDetection";
import type {
  AgentMiniappAnalyticsEventName,
  AgentMiniappAnalyticsEventParamsOf,
} from "./events";
import type {
  RealtimeConnectionOptions,
  ResolvedAuthToken,
} from "./realtime-connection";
import type { RequestOptions } from "./request";

import { createAgentCoordinatorName } from "replo-utils/lib/agentCoordinatorName";
import { isNetworkErrorMessage } from "replo-utils/lib/errors";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { errorUserFacingDetailsSchema } from "schemas/errors";
import { z } from "zod";

import { createAccountClient } from "./account";
import { BedrockAgent } from "./agent/bedrock-agent";
import { ProjectCoordinator } from "./agent/project-coordinator";
import { createAgentMemoryConfigClient } from "./agentMemoryConfig";
import { createAgentPermissionPoliciesClient } from "./agentPermissionPolicies";
import { createAnalyticsClient } from "./analytics";
import { createAssetsClient } from "./assets";
import { getMiniappToken } from "./auth";
import { createBillingClient } from "./billing";
import { createCheckoutSettingsClient } from "./checkoutSettings";
import { createCustomDomainsClient } from "./customDomains";
import { createEmailTemplatesClient } from "./emailTemplates";
import { BedrockApiRequestError, BedrockConfigError } from "./errors";
import { createFilesClient } from "./files";
import { createDriveFs, createSandboxFs } from "./fs";
import { getAgentRoutingParams, parseAgentRoutingFields } from "./host-params";
import { createIntegrationsClient } from "./integrations";
import { parentRemote } from "./miniappRpc";
import { createOAuthConnectedAppsClient } from "./oauthConnectedApps";
import { createPaymentProviderAccountClient } from "./paymentProviderAccount";
import { adoptPreconnectedAgentConnection } from "./preconnect";
import { createCommerceClient } from "./products";
import { createProjectMembershipClient } from "./projectMembership";
import { createProjectsClient } from "./projects";
import { createPublicApiTokensClient } from "./publicApiTokens";
import { RealtimeConnection } from "./realtime-connection";
import { createReferralsClient } from "./referrals";
import { createRegistryClient } from "./registry";
import { createSettingsClient } from "./settings";
import { createShippingOptionsClient } from "./shippingOptions";
import { createSitesClient } from "./sites";
import { createStarterSkillsClient } from "./starterSkills";
import { createTasksClient } from "./tasks";
import { createTeamUsageClient } from "./teamUsage";
import { createUserProjectNotificationSettingsClient } from "./userProjectNotificationSettings";
import { createWorkspaceMembershipClient } from "./workspaceMembership";
import { createWorkspacesClient } from "./workspaces";

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

// NOTE (Ben, 2026-06-24): Zod 4's z.toJSONSchema clones and globally-registers the
// schema tree on every call, so repeated conversions of the same schema leak. Memoize
// by schema identity; the WeakMap lets cached results be collected with the schema.
const jsonSchemaCache = new WeakMap<z.ZodType<unknown>, unknown>();
function toJSONSchemaCached(schema: z.ZodType<unknown>): unknown {
  const cached = jsonSchemaCache.get(schema);
  if (cached !== undefined) {
    return cached;
  }
  const jsonSchema = z.toJSONSchema(schema);
  jsonSchemaCache.set(schema, jsonSchema);
  return jsonSchema;
}

// `data` is optional: a missing key is a valid "no data" response (zod 4.4.x
// stopped treating a bare z.unknown() field as accepting a missing key).
const bedrockSuccessSchema = z.object({ data: z.unknown().optional() });

/**
 * Bedrock-api uses the shared `createErrorHandler` from `worker-utils`, which
 * emits two response shapes:
 *
 *  - `ReploHTTPError` / `Uncommon_GloballyMappedError` / `ReploAuthenticationError`:
 *    `userFacingDetails` is serialized as the body (`{ type, key, message, ... }`).
 *  - Anything else (uncaught throws): the 500 fallback shape
 *    `{ error, message, errorId }` where `error` is the literal string
 *    `"InternalError"`.
 *
 * We accept both and normalize them onto `BedrockApiRequestError.code`.
 */
const internalErrorSchema = z.object({
  error: z.string(),
  errorId: z.string().optional(),
  message: z.string(),
});

function buildUrl({
  baseUrl,
  path,
  query,
}: Pick<RequestOptions, "path" | "query"> & { baseUrl: string }) {
  const url = new URL(`${baseUrl}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined) {
        continue;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          url.searchParams.append(key, item);
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function toWebSocketUrl(httpUrl: string) {
  return httpUrl.replace(/^http/, "ws");
}

// Routing fields for the direct agent-coordinator DO name, from explicit
// options or the host-URL fallback.
function resolveRoutingFields(routing: RealtimeConnectionOptions["routing"]): {
  devAlias: string | null;
  initialSnapshotRef: string | null;
} {
  if (routing) {
    return {
      devAlias: routing.devAlias ?? null,
      initialSnapshotRef: routing.initialSnapshotRef ?? null,
    };
  }
  return parseAgentRoutingFields(getAgentRoutingParams());
}

export type BedrockTokenProvider = (options?: {
  forceRefreshToken?: boolean;
}) => string | Promise<string>;

export type BedrockClientAuth =
  | { type: "public"; projectId?: null }
  | {
      type: "miniapp";
      projectId: string;
      userId: string;
    }
  | {
      type: "browser";
      projectId: string;
      userId: string;
      getToken: BedrockTokenProvider;
    };

export interface BedrockClientOptions {
  bedrockApiUrl: string;
  agentCoordinatorBaseUrl: string;
  appId: string;
  auth: BedrockClientAuth;
  defaultHeaders?:
    | Record<string, string>
    | (() => Record<string, string | undefined>);
}

// Allowed values for the `orders.find` filter parameters. Exported as `const`
// arrays (not just literal types) so consumers can validate untrusted input
// against the SDK's contract at runtime — e.g. when adapting a generic
// `where` clause from a query DSL.
export const BEDROCK_ORDER_FINANCIAL_STATUSES = [
  "pending",
  "paid",
  "failed",
  "refunded",
  "partiallyRefunded",
] as const;
export type BedrockOrderFinancialStatus =
  (typeof BEDROCK_ORDER_FINANCIAL_STATUSES)[number];

export const BEDROCK_ORDER_FULFILLMENT_STATUSES = [
  "unfulfilled",
  "partial",
  "fulfilled",
  "cancelled",
] as const;
export type BedrockOrderFulfillmentStatus =
  (typeof BEDROCK_ORDER_FULFILLMENT_STATUSES)[number];

// Allowed reasons for `orders.refund`. Mirrors Stripe's refund reasons that
// the server forwards; exported as a `const` array so consumers can use the
// values to populate UI (e.g. Combobox items) without re-declaring the union.
export const BEDROCK_REFUND_REASONS = [
  "duplicate",
  "fraudulent",
  "requested_by_customer",
] as const;
export type BedrockRefundReason = (typeof BEDROCK_REFUND_REASONS)[number];

export class BedrockClient {
  readonly bedrockApiUrl: string;
  readonly agentCoordinatorBaseUrl: string;
  readonly appId: string;
  readonly auth: BedrockClientAuth;
  private readonly defaultHeaders:
    | Record<string, string>
    | (() => Record<string, string | undefined>)
    | undefined;

  constructor(options: BedrockClientOptions) {
    if (!options.bedrockApiUrl) {
      throw new BedrockConfigError({
        message:
          "BEDROCK_API_URL is required. It should be set by the dev orchestrator in dev (bin/dev-start.mts) and Doppler in prod.",
      });
    }
    if (!options.agentCoordinatorBaseUrl) {
      throw new BedrockConfigError({
        message:
          "agentCoordinatorBaseUrl is required. It should be set by the dev orchestrator in dev (bin/dev-start.mts) and Doppler in prod.",
      });
    }
    this.bedrockApiUrl = options.bedrockApiUrl;
    this.agentCoordinatorBaseUrl = options.agentCoordinatorBaseUrl;
    this.appId = options.appId;
    this.auth = options.auth;
    this.defaultHeaders = options.defaultHeaders;
  }

  database = {
    collections: {
      list: (input: { projectId: string }) => {
        return this.request<CatalogResponse>({
          method: "GET",
          path: `/api/v1/projects/${input.projectId}/database/collections`,
        });
      },
      create: async (input: { projectId: string } & CreateCollectionBody) => {
        const { projectId, ...body } = input;
        const { collection } = await this.request<{
          collection: CollectionMetadata;
        }>({
          method: "POST",
          path: `/api/v1/projects/${projectId}/database/collections`,
          body,
        });
        return collection;
      },
      update: async (input: { projectId: string } & UpdateCollectionInput) => {
        const { projectId, collectionKey, ...body } = input;
        const { collection } = await this.request<{
          collection: CollectionMetadata;
        }>({
          method: "PATCH",
          path: `/api/v1/projects/${projectId}/database/collections/${encodePathSegment(collectionKey)}`,
          body,
        });
        return collection;
      },
      publish: (input: { projectId: string } & PublishCollectionInput) => {
        return this.request<PublishCollectionResponse>({
          method: "POST",
          path: `/api/v1/projects/${input.projectId}/database/collections/${encodePathSegment(input.collectionKey)}/publish`,
        });
      },
      getSchema: (input: { projectId: string } & CollectionSchemaInput) => {
        return this.request<CollectionSchemaSnapshot>({
          method: "GET",
          path: `/api/v1/projects/${input.projectId}/database/collections/${encodePathSegment(input.collectionKey)}/schema`,
          query: { variant: input.variant },
        });
      },
    },
    fields: {
      create: async (
        input: { projectId: string } & CreateCollectionFieldInput,
      ) => {
        const { collection } = await this.request<{
          collection: CollectionMetadata;
        }>({
          method: "POST",
          path: `/api/v1/projects/${input.projectId}/database/collections/${encodePathSegment(input.collectionKey)}/fields`,
          body: input.field,
        });
        return collection;
      },
      update: async (
        input: { projectId: string } & UpdateCollectionFieldInput,
      ) => {
        const { projectId, collectionKey, key, ...body } = input;
        const { collection } = await this.request<{
          collection: CollectionMetadata;
        }>({
          method: "PATCH",
          path: `/api/v1/projects/${projectId}/database/collections/${encodePathSegment(collectionKey)}/fields/${encodePathSegment(key)}`,
          body,
        });
        return collection;
      },
      delete: async (
        input: { projectId: string } & DeleteCollectionFieldInput,
      ) => {
        const { collection } = await this.request<{
          collection: CollectionMetadata;
        }>({
          method: "DELETE",
          path: `/api/v1/projects/${input.projectId}/database/collections/${encodePathSegment(input.collectionKey)}/fields/${encodePathSegment(input.key)}`,
        });
        return collection;
      },
    },
    records: {
      create: async (input: { projectId: string } & CreateRecordInput) => {
        const {
          projectId,
          collectionKey,
          schemaVariant,
          schemaRevision,
          ...body
        } = input;
        const { record } = await this.request<{ record: RecordResponse }>({
          method: "POST",
          path: `/api/v1/projects/${projectId}/database/collections/${encodePathSegment(collectionKey)}/records`,
          query: { schemaVariant, schemaRevision },
          body,
        });
        return record;
      },
      get: async (input: { projectId: string } & GetRecordInput) => {
        const { record } = await this.request<{ record: RecordResponse }>({
          method: "GET",
          path: `/api/v1/projects/${input.projectId}/database/collections/${encodePathSegment(input.collectionKey)}/records/${encodePathSegment(input.recordId)}`,
        });
        return record;
      },
      update: async (input: { projectId: string } & UpdateRecordInput) => {
        const {
          projectId,
          collectionKey,
          recordId,
          schemaVariant,
          schemaRevision,
          ...body
        } = input;
        const { record } = await this.request<{ record: RecordResponse }>({
          method: "PUT",
          path: `/api/v1/projects/${projectId}/database/collections/${encodePathSegment(collectionKey)}/records/${encodePathSegment(recordId)}`,
          query: { schemaVariant, schemaRevision },
          body,
        });
        return record;
      },
      delete: (input: { projectId: string } & DeleteRecordInput) => {
        return this.request<DeleteRecordResponse>({
          method: "DELETE",
          path: `/api/v1/projects/${input.projectId}/database/collections/${encodePathSegment(input.collectionKey)}/records/${encodePathSegment(input.recordId)}`,
        });
      },
      query: async (input: { projectId: string } & QueryRecordsInput) => {
        const { projectId, collectionKey, ...body } = input;
        const { records } = await this.request<{ records: RecordResponse[] }>({
          method: "POST",
          path: `/api/v1/projects/${projectId}/database/collections/${encodePathSegment(collectionKey)}/records/query`,
          body,
        });
        return records;
      },
    },
  };

  llm = {
    extract: async <SchemaType>(
      input: {
        projectId: string;
        schema: z.ZodType<SchemaType>;
        /**
         * Required cache namespace. The server hashes (files, instruction,
         * schema, model, maxTokens) and reads/writes the result under
         * `llm:extract:v1:<cacheKey>:<hash>`. Use a stable identifier that
         * scopes the cache to your caller — e.g.
         * `site-settings.layout-scripts.${projectId}`.
         */
        cacheKey: string;
        files?: { path: string; content: string }[];
        instruction?: string;
        schemaName?: string;
        schemaDescription?: string;
        model?: string;
        maxTokens?: number;
        /**
         * Skip the cache read — always make a fresh LLM call. Independent
         * of `bypassCacheWrite`: a fresh result is still persisted unless
         * write is also bypassed.
         */
        bypassCacheRead?: boolean;
      } & (
        | {
            /**
             * Skip the cache write — never persist this call's result.
             * Independent of `bypassCacheRead`.
             */
            bypassCacheWrite: true;
          }
        | {
            bypassCacheWrite?: false;
            /**
             * KV cache TTL in milliseconds. Required when writing is enabled
             * — no implicit default, since different flows tolerate very
             * different staleness. Minimum 60_000 (Cloudflare KV limit).
             */
            cacheTtlMs: number;
          }
      ),
    ) => {
      // NOTE (Ben, 2026-04-30, REPL-26532): Widen the schema to a non-generic type before z.toJSONSchema so the caller's deep zod-inferred type doesn't propagate into the conversion.
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      const widenedSchema = input.schema as z.ZodType<unknown>;
      const response = await this.request<{
        data: unknown;
        model: string;
        usage?: { inputTokens?: number; outputTokens?: number };
        cached?: boolean;
      }>({
        method: "POST",
        path: "/api/v1/llm/extract",
        query: { projectId: input.projectId },
        body: {
          jsonSchema: toJSONSchemaCached(widenedSchema),
          files: input.files ?? [],
          instruction: input.instruction,
          schemaName: input.schemaName,
          schemaDescription: input.schemaDescription,
          model: input.model,
          maxTokens: input.maxTokens,
          cacheKey: input.cacheKey,
          bypassCacheRead: input.bypassCacheRead,
          ...(input.bypassCacheWrite === true
            ? { bypassCacheWrite: true as const }
            : {
                bypassCacheWrite: input.bypassCacheWrite,
                cacheTtlMs: input.cacheTtlMs,
              }),
        },
      });
      return { ...response, data: input.schema.parse(response.data) };
    },

    complete: (input: {
      projectId: string;
      prompt: string;
      files?: { path: string; content: string }[];
      system?: string;
      model?: string;
      maxTokens?: number;
    }) => {
      return this.request<{
        text: string;
        model: string;
        usage?: { inputTokens?: number; outputTokens?: number };
      }>({
        method: "POST",
        path: "/api/v1/llm/complete",
        query: { projectId: input.projectId },
        body: {
          prompt: input.prompt,
          files: input.files ?? [],
          system: input.system,
          model: input.model,
          maxTokens: input.maxTokens,
        },
      });
    },
  };

  /**
   * Client for `bedrock-api`'s registry routes (`/api/v1/registry/*`):
   * project-scoped and public registry items (templates, skills, branding).
   */
  registry = createRegistryClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  // Server-verified activation checklist state, shared by the home "Get set
  // up" cards and the Launchpad resource surface. `reconcile` also grants any
  // newly-earned rewards (idempotently).
  activation = {
    get: ({ projectId }: { projectId: string }): Promise<ActivationState> => {
      return this.request<ActivationState>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/activation`,
      });
    },

    reconcile: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<ActivationState> => {
      return this.request<ActivationState>({
        method: "POST",
        path: `/api/v1/projects/${projectId}/activation/reconcile`,
      });
    },
  };

  // One scrape of the merchant's own site, read for brand basics and for the
  // integrations they already run, so onboarding can pre-fill instead of ask.
  siteDetection = {
    detect: ({
      projectId,
      url,
    }: {
      projectId: string;
      url: string;
    }): Promise<SiteDetection> => {
      return this.request<SiteDetection>({
        method: "POST",
        path: "/api/v1/site-detection/detect",
        body: { projectId, url },
      });
    },
  };

  // Tools found on the merchant's own site at signup, joined to what they have
  // since connected.
  detectedIntegrations = {
    get: ({
      projectId,
    }: {
      projectId: string;
    }): Promise<DetectedIntegrations> => {
      return this.request<DetectedIntegrations>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/detected-integrations`,
      });
    },
  };

  // The generated brand, for showing it back to the merchant who now owns it.
  brandPreview = {
    get: ({ projectId }: { projectId: string }): Promise<BrandPreview> => {
      return this.request<BrandPreview>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/brand-preview`,
      });
    },
  };

  // How far the merchant's brand has got, while it is being built.
  brandPregenStatus = {
    get: ({ projectId }: { projectId: string }): Promise<BrandPregenStatus> => {
      return this.request<BrandPregenStatus>({
        method: "GET",
        path: `/api/v1/projects/${projectId}/brand-pregen/status`,
      });
    },
  };

  orders = {
    find: (query: {
      projectId: string;
      financialStatus?: BedrockOrderFinancialStatus;
      fulfillmentStatus?: BedrockOrderFulfillmentStatus;
      customerEmail?: string;
      search?: string;
      cursor?: string;
      limit?: number;
    }): Promise<OrderListResponse> => {
      return this.request<OrderListResponse>({
        method: "GET",
        path: "/api/v1/orders",
        query,
      });
    },

    getOverview: (query: {
      projectId: string;
      range: "7d" | "30d" | "90d" | "all";
    }): Promise<OrderOverviewResponse> => {
      return this.request<OrderOverviewResponse>({
        method: "GET",
        path: "/api/v1/orders/overview",
        query,
      });
    },

    getById: ({
      orderId,
    }: {
      orderId: string;
    }): Promise<OrderDetailResponse> => {
      return this.request<OrderDetailResponse>({
        method: "GET",
        path: `/api/v1/orders/${orderId}`,
      });
    },

    refund: (input: {
      orderId: string;
      lineItemIds?: string[];
      /** Integer minor units (Stripe convention) in the order's currency */
      amount?: number;
      reason?: BedrockRefundReason;
    }): Promise<RefundResponse> => {
      const { orderId, ...body } = input;
      return this.request<RefundResponse>({
        method: "POST",
        path: `/api/v1/orders/${orderId}/refund`,
        body,
      });
    },

    createFulfillment: (input: {
      fulfillmentOrderId: string;
      /** Omitted = fulfill everything remaining on the fulfillment order. */
      items?: { orderItemId: string; quantity?: number }[];
      trackingNumber?: string;
      trackingUrl?: string;
      trackingCarrier?: string;
    }): Promise<FulfillmentResponse> => {
      const { fulfillmentOrderId, ...body } = input;
      return this.request<FulfillmentResponse>({
        method: "POST",
        path: `/api/v1/orders/fulfillment-orders/${fulfillmentOrderId}/fulfillments`,
        body,
      });
    },

    events: {
      list: (input: {
        orderId: string;
        cursor?: string;
        limit?: number;
      }): Promise<OrderEventListResponse> => {
        const { orderId, cursor, limit } = input;
        return this.request<OrderEventListResponse>({
          method: "GET",
          path: `/api/v1/orders/${orderId}/events`,
          query: { cursor, limit },
        });
      },

      addNote: (input: {
        orderId: string;
        markdown: string;
        fulfillmentOrderId?: string;
        fulfillmentId?: string;
      }): Promise<OrderEventResponse> => {
        const { orderId, ...body } = input;
        return this.request<OrderEventResponse>({
          method: "POST",
          path: `/api/v1/orders/${orderId}/notes`,
          body,
        });
      },

      editNote: (input: {
        eventId: string;
        markdown: string;
      }): Promise<OrderEventResponse> => {
        const { eventId, markdown } = input;
        return this.request<OrderEventResponse>({
          method: "PATCH",
          path: `/api/v1/orders/notes/${eventId}`,
          body: { markdown },
        });
      },

      deleteNote: ({
        eventId,
      }: {
        eventId: string;
      }): Promise<OrderEventResponse> => {
        return this.request<OrderEventResponse>({
          method: "DELETE",
          path: `/api/v1/orders/notes/${eventId}`,
        });
      },
    },
  };

  fulfillmentOrders = {
    cancel: ({
      fulfillmentOrderId,
    }: {
      fulfillmentOrderId: string;
    }): Promise<FulfillmentOrderResponse> => {
      return this.request<FulfillmentOrderResponse>({
        method: "POST",
        path: `/api/v1/orders/fulfillment-orders/${fulfillmentOrderId}/cancel`,
      });
    },
  };

  /**
   * Aggregate project settings bootstrap for the settings mini-app.
   */
  settings = createSettingsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  starterSkills = createStarterSkillsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * Per-member agent usage rollup for the Team Usage settings tab.
   */
  teamUsage = createTeamUsageClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  billing = createBillingClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  agentPermissionPolicies = createAgentPermissionPoliciesClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  userProjectNotificationSettings = createUserProjectNotificationSettingsClient(
    {
      request: <ResponseType>(options: RequestOptions) => {
        return this.request<ResponseType>(options);
      },
    },
  );

  agentMemoryConfig = createAgentMemoryConfigClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * NOTE (Max, 2026-05-04): Project metadata reads (shop subdomain, custom domain, favicons).
   */
  projects = createProjectsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * NOTE (Max, 2026-07-22): Project payment-account lifecycle (set up /
   * resume onboarding, status, provider dashboard link, disconnect) backed
   * by `/api/v1/projects/{projectId}/payment-provider-account`.
   */
  paymentProviderAccount = createPaymentProviderAccountClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * NOTE (Max, 2026-07-29): Merchant checkout-session settings (shipping
   * countries/rates, automatic tax, promo codes) backed by
   * `/api/v1/projects/{projectId}/checkout-settings`.
   */
  checkoutSettings = createCheckoutSettingsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * NOTE (Max, 2026-08-04): Checkout shipping options (Medusa-style option +
   * rules, minor-unit amounts) backed by
   * `/api/v1/projects/{projectId}/shipping-options`.
   */
  shippingOptions = createShippingOptionsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * NOTE (Cole, 2026-06-02): Site-scoped custom domain management (list / add /
   * verify / delete) backed by `/api/v1/custom-domains`. Mirrors the
   * custom-domain MCP tools for the site-settings UI.
   */
  customDomains = createCustomDomainsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * Agent email builder template CRUD + render/send (`/api/v1/email-templates/*`).
   */
  emailTemplates = createEmailTemplatesClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  workspaces = createWorkspacesClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  workspaceMembership = createWorkspaceMembershipClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  projectMembership = createProjectMembershipClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  account = createAccountClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  oauthConnectedApps = createOAuthConnectedAppsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  publicApiTokens = createPublicApiTokensClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  referrals = createReferralsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * Raw ClickHouse passthrough for the project's Replo analytics data
   * (sessions, page views, purchases, etc.). Mirrors the
   * `query_replo_analytics` MCP tool — see the `querying-analytics`
   * skill for table schemas and example queries.
   */
  analytics = createAnalyticsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  tasks = createTasksClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  // Project sites straight from Postgres (no sandbox); `agent.sites` carries sandbox checkout state.
  sites = createSitesClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  assets = createAssetsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
    requestBlob: (options: RequestOptions) => this.requestBlob(options),
  });

  files = createFilesClient({
    request: <ResultType>(options: RequestOptions) => {
      return this.request<ResultType>(options);
    },
  });

  integrations = createIntegrationsClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  /**
   * Provider-agnostic commerce surface (catalog browsing, mockup generation,
   * agent product CRUD). Implementation lives in `bedrock-api`'s
   * `/api/v1/commerce/*` routes.
   */
  commerce = createCommerceClient({
    request: <ResponseType>(options: RequestOptions) => {
      return this.request<ResponseType>(options);
    },
  });

  track<EventName extends AgentMiniappAnalyticsEventName>(input: {
    name: EventName;
    properties: AgentMiniappAnalyticsEventParamsOf<EventName>;
  }) {
    parentRemote.trackEvent(input);
  }

  pushMeasurement(input: {
    type: string;
    values: Record<string, number>;
    context?: Record<string, string>;
  }) {
    parentRemote.pushMeasurement(input);
  }

  connectRealtime(options?: RealtimeConnectionOptions): RealtimeConnection {
    const coordinatorWsBaseUrl = toWebSocketUrl(this.agentCoordinatorBaseUrl);
    // (Andrew, 2026-07-29): Adopt the navigation-time dial when it targets the exact DO this call would dial.
    if (this.auth.type === "browser") {
      const preconnected = adoptPreconnectedAgentConnection({
        projectId: this.auth.projectId,
        actorId: this.auth.userId,
        agentCoordinatorBaseUrl: this.agentCoordinatorBaseUrl,
        options,
      });
      if (preconnected) {
        return preconnected;
      }
    }
    return new RealtimeConnection({
      resolveAuthToken: (authOptions) => this.resolveAuthToken(authOptions),
      buildWebsocketUrl: (auth) => {
        if (!auth.projectId || !auth.userId) {
          throw new BedrockApiRequestError({
            code: "UNAUTHORIZED",
            status: 0,
            message:
              "Realtime connection requires a projectId and userId on the token",
          });
        }
        const routing = resolveRoutingFields(options?.routing);
        // createAgentCoordinatorName already returns the URL-safe encoded form —
        // re-encoding here would double-encode and route to a different DO.
        const name = createAgentCoordinatorName({
          projectId: auth.projectId,
          actorId: auth.userId,
          devAlias: routing.devAlias,
          initialSnapshotRef: routing.initialSnapshotRef,
        });
        return `${coordinatorWsBaseUrl}/agents/replo-agent/${name}`;
      },
      ...options,
    });
  }

  // The project-coordinator DO is project-scoped (keyed by projectId only, no
  // actor/snapshot) and lives on bedrock-api, not the agent coordinator. It
  // shares the same RealtimeConnection transport, so RPCs are available the day
  // the DO implements them.
  connectProjectCoordinator(
    options?: Pick<
      RealtimeConnectionOptions,
      "onRawMessage" | "onOpen" | "onClose" | "onError"
    >,
  ): RealtimeConnection {
    const coordinatorWsBaseUrl = toWebSocketUrl(this.bedrockApiUrl);
    return new RealtimeConnection({
      resolveAuthToken: (authOptions) => this.resolveAuthToken(authOptions),
      // (Max, 2026-07-02, REPL-28703): The server resolves the per-project DO
      // from this explicit query param (idFromName); the token carries identity only.
      buildWebsocketUrl: (auth) => {
        if (!auth.projectId) {
          throw new BedrockApiRequestError({
            code: "UNAUTHORIZED",
            status: 0,
            message: "Project coordinator connection requires a projectId",
          });
        }
        const url = new URL(
          `${coordinatorWsBaseUrl}/api/v1/project-coordinator/ws`,
        );
        url.searchParams.set("projectId", auth.projectId);
        return url.toString();
      },
      autoReconnect: true,
      ...options,
    });
  }

  // NOTE (Max, 2026-04-29): Provides typed RPC actions and
  // session streaming over the WebSocket connection to the coordinator DO.
  // This is pure TS — no React. React consumers access it via useBedrock().agent.
  // non-React consumers (CLI / Slack workers) access it via new BedrockClient(options).agent.
  agent = new BedrockAgent(this);

  // Pure-TS handle to the project-coordinator DO (project-wide files-changed +
  // presence), disjoint from the agent/compute socket above. React consumers
  // reach it via useBedrock().client.projectCoordinator; non-React via the client.
  projectCoordinator = new ProjectCoordinator(this);

  // Two backings of one `AgentFilesystem`: `sandbox.fs` rides booted-sandbox RPCs,
  // `drive.fs` hits the path-addressed routes with no sandbox. Verbs/paths are identical.
  sandbox = { fs: createSandboxFs(this.agent) };

  drive = {
    fs: createDriveFs({
      request: <ResultType>(options: RequestOptions) => {
        return this.request<ResultType>(options);
      },
      getProjectId: () => this.requireProjectId(),
    }),
  };

  // `drive.fs` is project-scoped per call, so a call without a project on the
  // client's auth is a programming error, not a silent cross-project read.
  private requireProjectId(): string {
    const projectId = this.auth.projectId ?? null;
    if (!projectId) {
      throw new BedrockApiRequestError({
        code: "UNAUTHORIZED",
        status: 0,
        message: "client.drive.fs requires a projectId on the client auth",
      });
    }
    return projectId;
  }

  private async request<ResponseType>(
    options: RequestOptions,
  ): Promise<ResponseType> {
    const firstResponse = await this._send({
      ...options,
      forceRefreshToken: false,
    });
    if (firstResponse.status !== 401 || this.auth.type === "public") {
      return this._parseResponse<ResponseType>(firstResponse);
    }
    const retryResponse = await this._send({
      ...options,
      forceRefreshToken: true,
    });
    return this._parseResponse<ResponseType>(retryResponse);
  }

  // Binary sibling of `request`: returns the raw response body as a Blob
  // instead of parsing JSON. Used for endpoints that stream bytes (e.g. asset
  // content). Shares the same auth + 401-refresh-retry flow.
  private async requestBlob(options: RequestOptions): Promise<Blob> {
    const firstResponse = await this._send({
      ...options,
      forceRefreshToken: false,
    });
    const response =
      firstResponse.status === 401 && this.auth.type !== "public"
        ? await this._send({ ...options, forceRefreshToken: true })
        : firstResponse;
    if (!response.ok) {
      // `_parseResponse` throws a `BedrockApiRequestError` for any non-2xx
      // body, so this never actually returns.
      return this._parseResponse<Blob>(response);
    }
    return response.blob();
  }

  private async _send(
    options: RequestOptions & { forceRefreshToken: boolean },
  ): Promise<Response> {
    const auth = await this.resolveAuthToken({
      forceRefreshToken: options.forceRefreshToken,
    });
    const headers: Record<string, string> = {
      ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
      ...this.buildDefaultHeaders(),
    };
    let body: BodyInit | undefined;
    if (options.binaryBody !== undefined) {
      body = options.binaryBody;
    } else if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
    if (options.headers) {
      Object.assign(headers, options.headers);
    }
    // NOTE (Ryan, 2026-07-20): browser/extension fetch rejections are client
    // connectivity noise; surface them as structured SDK errors so callers and
    // Sentry can treat them as BedrockApiRequestError instead of raw TypeErrors.
    try {
      return await fetch(
        buildUrl({
          baseUrl: this.bedrockApiUrl,
          path: options.path,
          query: options.query,
        }),
        {
          method: options.method,
          headers,
          body,
        },
      );
    } catch (error) {
      if (error instanceof Error && isNetworkErrorMessage(error.message)) {
        throw new BedrockApiRequestError({
          code: "NETWORK_ERROR",
          status: 0,
          message: error.message,
        });
      }
      throw error;
    }
  }

  private buildDefaultHeaders(): Record<string, string> {
    if (!this.defaultHeaders) {
      return {};
    }
    const headers =
      typeof this.defaultHeaders === "function"
        ? this.defaultHeaders()
        : this.defaultHeaders;
    const normalizedHeaders = Object.entries(headers).reduce<
      Record<string, string>
    >((accumulator, [key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
    return normalizedHeaders;
  }

  private resolveAuthToken({
    forceRefreshToken,
  }: {
    forceRefreshToken: boolean;
  }): Promise<ResolvedAuthToken> {
    return exhaustiveSwitch(this.auth)({
      public: async () => ({}),
      browser: async (auth) => ({
        token: await auth.getToken({ forceRefreshToken }),
        projectId: auth.projectId,
        userId: auth.userId,
      }),
      miniapp: async (auth) => ({
        token: await getMiniappToken({
          forceRefresh: forceRefreshToken,
        }),
        projectId: auth.projectId,
        userId: auth.userId,
      }),
    });
  }

  private async _parseResponse<ResponseType>(
    response: Response,
  ): Promise<ResponseType> {
    if (response.status === 204) {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      return null as ResponseType;
    }

    let rawJson: unknown;
    try {
      rawJson = await response.json();
    } catch {
      throw new BedrockApiRequestError({
        code: "INVALID_RESPONSE",
        status: response.status,
        message: `Server returned non-JSON response (status ${response.status})`,
      });
    }

    if (!response.ok) {
      // (Cole, 2026-07-08, REPL-28948): "unknown" is the server's unstamped sentinel.
      const errorIdParse = z.object({ errorId: z.string() }).safeParse(rawJson);
      const reploErrorId =
        errorIdParse.success && errorIdParse.data.errorId !== "unknown"
          ? errorIdParse.data.errorId
          : undefined;
      const parsedUserFacing = errorUserFacingDetailsSchema.safeParse(rawJson);
      if (parsedUserFacing.success) {
        throw new BedrockApiRequestError({
          code: parsedUserFacing.data.key,
          status: response.status,
          message:
            parsedUserFacing.data.message ??
            `Request failed with status ${response.status}`,
          reploErrorId,
        });
      }
      const parsedInternal = internalErrorSchema.safeParse(rawJson);
      if (parsedInternal.success) {
        throw new BedrockApiRequestError({
          code: parsedInternal.data.error,
          status: response.status,
          message: `${parsedInternal.data.message}${parsedInternal.data.errorId ? ` (error id ${parsedInternal.data.errorId})` : ""}`,
          reploErrorId,
        });
      }
      throw new BedrockApiRequestError({
        code: response.status === 401 ? "UNAUTHORIZED" : "UNKNOWN_ERROR",
        status: response.status,
        message: `Request failed with status ${response.status}`,
        reploErrorId,
      });
    }

    const parsedSuccess = bedrockSuccessSchema.safeParse(rawJson);
    if (!parsedSuccess.success) {
      throw new BedrockApiRequestError({
        code: "INVALID_RESPONSE",
        status: response.status,
        message: `Unexpected response format: ${JSON.stringify(rawJson)}`,
      });
    }
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    return parsedSuccess.data.data as ResponseType;
  }
}
